/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import JsBarcode from 'jsbarcode';
import jsPDF from 'jspdf';
import * as React from 'react';
import { when } from 'mobx';

import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { IDlvryProductSheetData } from './dlvry-product-sheet.data';
import { IDlvryProductSheetProps, IDlvryProductSheetResources } from './dlvry-product-sheet.props.autogenerated';
import { getValidProductImages } from '@msdyn365-commerce-modules/buybox/dist/lib/modules/quickview/utilities/get-valid-product-images';
import { getAttributesLocale, getAttributeStringOrDefault, parseAttributesMap } from '../../shared-utils/attributes';
import { getTemperatureZone } from '../../shared-utils/attributes/get-temperature-zone';
import { humanizeUnitOfMeasure } from '../../components/unit-of-measure';
import { AttributeKey } from '../../shared-utils/types';

export interface IDlvryProductSheetViewProps extends IDlvryProductSheetProps<IDlvryProductSheetData> {
    ProductSheet: IModuleProps;
    className: string;
    resources: IDlvryProductSheetResources;
    onDownloadProductSheet: () => void;
}

export interface IDlvryProductSheetState {
    imageUrl: string;
    retrievedImageUrl: boolean;
}

/**
 *
 * DlvryProductSheet component
 * @extends {React.PureComponent<IDlvryProductSheetProps<IDlvryProductSheetData>>}
 */
class DlvryProductSheet extends React.PureComponent<IDlvryProductSheetProps<IDlvryProductSheetData>, IDlvryProductSheetState> {
    private defaultThumbnailImageSettings: {
        viewports: {
            xs: { q: string; w: number; h: number; };
            lg: { q: string; w: number; h: number; };
        };
        lazyload: boolean;
        cropFocalRegion: boolean;
    };
    constructor(props: IDlvryProductSheetProps<IDlvryProductSheetData>) {
        super(props);

        this.state = {
            imageUrl: '',
            retrievedImageUrl: false
        };

        this.__downloadProductSheet = this.__downloadProductSheet.bind(this);
        this.__createProductSheet = this.__createProductSheet.bind(this);
        this.__getAttributeValueAsString = this.__getAttributeValueAsString.bind(this);

        this.defaultThumbnailImageSettings = {
            viewports: {
                xs: { q: '100', w: 60, h: 60 },
                lg: { q: '100', w: 60, h: 60 }
            },
            lazyload: true,
            cropFocalRegion: true
        };
    }

    public componentDidMount(): void {
        const {
            context,
            data: { product }
        } = this.props;

        when(
            () => product.result !== undefined,
            async () => {
                const result = await getValidProductImages(
                    product.result?.RecordId || 0,
                    +context.request.apiSettings.channelId,
                    context.actionContext,
                    this.defaultThumbnailImageSettings,
                    product?.result?.productVariant);

                const placeholderImageName = context.request.app.config?.placeholderImageName;
                let src = result?.[0]?.src;

                if (src?.includes('/search')) {
                    src = `${src}&w=230&h=230&m=1&cropfocalregion=true`;
                } else if (placeholderImageName && !src) {
                    let baseImageUrl = context.request.apiSettings.baseImageUrl;
                    if (baseImageUrl.endsWith('/')) {
                        baseImageUrl = baseImageUrl.slice(0, -1);
                    }
                    src = baseImageUrl += placeholderImageName;
                }

                this.setState({
                    imageUrl: src || '',
                    retrievedImageUrl: true
                });
            }
        );
    }

    public componentDidUpdate(prevProps: Readonly<IDlvryProductSheetProps<IDlvryProductSheetData>>, prevState: Readonly<IDlvryProductSheetState>, snapshot?: any): void {
        if (prevState.retrievedImageUrl === false && !this.state.retrievedImageUrl) {
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const downloadpdf = params.get('downloadpdf');

        // Allow either empty value, 'true' or '1' to download pdf
        if (downloadpdf === null || downloadpdf && downloadpdf.toLowerCase() !== 'true' && downloadpdf !== '1') {
            return;
        }

        void this.__downloadProductSheet();
    }

    public render(): JSX.Element | null {
        const productSheetViewProps: IDlvryProductSheetViewProps = {
            ...this.props,
            ProductSheet: {
                moduleProps: this.props,
                className: this.props.typeName
            },
            className: this.props.typeName,
            onDownloadProductSheet: this.__downloadProductSheet
        };
        return this.props.renderView(productSheetViewProps);
    }

    private async __downloadProductSheet() {
        try {
            const { productNumber, pdf } = await this.__createProductSheet();

            pdf.save(`DLVRY - ${productNumber}.pdf`);
        } catch (error) {
            console.error(error);
            alert(this.props.resources.productSheetErrorText);
        }
    }

    private __getAttributeValueAsString(name: AttributeKey, defaultValue: string): string;
    private __getAttributeValueAsString(name: AttributeKey, defaultValue?: undefined): string | undefined;
    private __getAttributeValueAsString(name: AttributeKey, defaultValue?: string): string | undefined {
        const {
            context,
            data: { productSpecificationData }
        } = this.props;
        const locale = context.request.locale;

        return getAttributeStringOrDefault(locale, productSpecificationData.result, name, defaultValue);
    }

    private async __createProductSheet(): Promise<{
        productNumber: string,
        pdf: jsPDF
    }> {
        if (!(window as any).jspdf) {
            throw new Error('jsPDF not found');
        }

        const { resources } = this.props;
        const productSpecificationData = this.props.data.productSpecificationData.result;
        const locale = getAttributesLocale(this.props.context.request.locale);
        // Use 'jsPDF' from 'window' object instead of NPM package because package isn't working atm
        const { jsPDF } = (window as any).jspdf;

        const doc: jsPDF = new jsPDF();
        const originalFontSize = doc.getFontSize();
        const originalFillColor = doc.getFillColor();

        // Attributes
        const productNumber = this.props.data.product.result?.ProductNumber || '-';
        const productName =  this.props.data.product.result?.Name || locale.notFound;
        const brand = this.__getAttributeValueAsString('brand');
        const manufacturerProductNumber = this.__getAttributeValueAsString('manufacturerProductNumber', locale.notFound);
        const manufacturer = this.__getAttributeValueAsString('manufacturer', locale.notFound);
        const salesUnit = humanizeUnitOfMeasure(this.props.context, this.props.data.product.result?.DefaultUnitOfMeasure);
        const temperatureZone = productSpecificationData
            ? getTemperatureZone(this.props.context.request.locale, productSpecificationData)
            : '-';
        const ingredients = this.__getAttributeValueAsString('ingredients', '-');
        const quantity = this.__getAttributeValueAsString('quantity', locale.notFound);

        const parsedAttributes = parseAttributesMap(this.props.context, productSpecificationData);
        const nutritionalContent = parsedAttributes.find(x => x.name === locale.groups.nutrients);
        const allergens = parsedAttributes.find(x => x.name === locale.groups.allergens);
        const productInformation = parsedAttributes.find(x => x.name === locale.groups.productInformation);
        const specialBadges = this.__getAttributeValueAsString('specialBadges')
            ?.split('|')
            .map(x => ({
                name: x,
                value: locale.labels.yes
            }))
            .filter(x => x.name);
        const url = window.location.origin;
        const timestamp = this.__getFormattedDate();

        // Print header
        doc.setFontSize(9);
        doc.text(resources.productSheetHeader.replace('{productNumber}', productNumber), 5, 5);
        doc.text(resources.productSheetHeaderRight.replace('{url}', url).replace('{timestamp}', timestamp), doc.internal.pageSize.width - 5, 5, {
            align: 'right'
        });
        doc.line(5, 7, doc.internal.pageSize.width - 5, 7);

        doc.setDrawColor('#CECECE');

        // Product details
        doc.setFontSize(originalFontSize);
        doc.text(productName, 5, 18);

        doc.setFontSize(originalFontSize - 6);
        if (brand) {
            doc.text(brand, 5, 26);
        }

        const textLines = (
            lines: {[key: string]: string},
            xPos1: number,
            xPos2:number,
            yPos: number,
            maxLen: number | undefined,
            lineHeight: number,
            boldKeys: boolean
        ) => {
            let y = yPos;
            Object.keys(lines).forEach(key => {
                const line = lines[key];

                if (boldKeys) {
                    doc.setFont(doc.getFont().fontName, 'bold');
                }

                doc.text(key, xPos1, y);

                if (boldKeys) {
                    doc.setFont(doc.getFont().fontName, 'normal');
                }

                if (maxLen) {
                    doc.splitTextToSize(line, maxLen).forEach((x: string) => {
                        doc.text(x, xPos2, y);
                        y += lineHeight;
                    });
                } else {
                    doc.text(line, xPos2, y);
                    y += lineHeight;
                }
            });
        };

        textLines({
            [resources.productSheetProductNumber]: productNumber,
            [locale.labels.manufacturerProductNumber]: manufacturerProductNumber,
            [locale.labels.manufacturer]: manufacturer,
            [resources.productSheetSalesUnit]: salesUnit,
            [resources.productSheetTemperatureZone]: temperatureZone || '-'
        }, 5, 46, 34, 80, 4, true);

        if (this.state.imageUrl) {
            const productImg = document.createElement('img');
            productImg.src = this.state.imageUrl;
            try {
                const imgSize = await this.__getImageNaturalSizeAsync(productImg);
                const size = this.__scaleToMax60(imgSize.width, imgSize.height);
                const leftoverSpace = 60 - size.width;
                const centerOffset = leftoverSpace > 0 ? leftoverSpace / 2 : 0;

                doc.addImage(
                    productImg,
                    'JPEG',
                    (doc.internal.pageSize.width - 65) + centerOffset,
                    10,
                    size.width,
                    size.height
                );
            } catch {
                doc.rect(doc.internal.pageSize.width - 65, 10, 60, 60);
            }
        }

        doc.line(5, 74, doc.internal.pageSize.width - 5, 74);

        doc.setFontSize(doc.getFontSize() - 1);
        doc.setFont(doc.getFont().fontName, 'bold');
        doc.text(locale.labels.ingredients, 5, 78);
        doc.setFont(doc.getFont().fontName, 'normal');
        if (ingredients) {
            (doc.splitTextToSize(ingredients, doc.internal.pageSize.width - 10) as string[]).forEach((x, i) => {
                doc.text(x, 5, 82 + (i * 3.5));
            });
        }
        doc.line(5, 98, doc.internal.pageSize.width - 5, 98);

        //doc.setFontSize(doc.getFontSize() - 1);

        doc.setFont(doc.getFont().fontName, 'bold');
        doc.text(locale.groups.nutrients, 5, 102);
        doc.setFont(doc.getFont().fontName, 'normal');

        const halfWidth = doc.internal.pageSize.width / 2;

        let nutritionalContentY = 106;
        nutritionalContent?.attributes.forEach((x, i) => {
            if ((i + 1) % 2 === 0) {
                doc.setFillColor('#F2F2F0');
                doc.rect(5, nutritionalContentY - 2.6, halfWidth - 10, 3, 'F');
            }
            doc.text(x.name, 5, nutritionalContentY);
            doc.text(x.value || '-', 65, nutritionalContentY);
            nutritionalContentY += 3.5;
        });

        doc.setFont(doc.getFont().fontName, 'bold');
        doc.text(locale.groups.allergens, halfWidth, 102);
        doc.setFont(doc.getFont().fontName, 'normal');

        let allergensY = 106;
        allergens?.attributes.forEach((x, i) => {
            if ((i + 1) % 2 === 0) {
                doc.setFillColor('#F2F2F0');
                doc.rect(halfWidth, allergensY - 2.6, halfWidth - 10, 3, 'F');
            }

            doc.text(x.name, halfWidth, allergensY);
            doc.text(x.value || '-', halfWidth + 55, allergensY);
            allergensY += 3.5;
        });

        doc.line(5, 203, doc.internal.pageSize.width - 5, 203);

        doc.setFont(doc.getFont().fontName, 'bold');
        doc.text(resources.productSheetSpecialBrands, 5, 208);
        doc.setFont(doc.getFont().fontName, 'normal');

        let specialBadgesY = 212;
        specialBadges?.forEach((x, i) => {
            if ((i + 1) % 2 === 0) {
                doc.setFillColor('#F2F2F0');
                doc.rect(5, specialBadgesY - 2.6, halfWidth - 10, 3, 'F');
            }
            doc.text(x.name, 5, specialBadgesY);
            doc.text(x.value, 60, specialBadgesY);
            specialBadgesY += 3.5;
        });

        doc.setFont(doc.getFont().fontName, 'bold');
        doc.text(resources.productSheetProductInformation, halfWidth, 208);
        doc.setFont(doc.getFont().fontName, 'normal');

        let productInformationY = 212;
        productInformation?.attributes.forEach((x, i) => {
            if ((i + 1) % 2 === 0) {
                doc.setFillColor('#F2F2F0');
                doc.rect(halfWidth + 5, productInformationY - 2.6, halfWidth - 10, 3, 'F');
            }
            doc.text(x.name, halfWidth, productInformationY);
            doc.text(x.value, halfWidth + 55, productInformationY);
            productInformationY += 3.5;
        });

        doc.line(5, 234, doc.internal.pageSize.width - 5, 234);
        doc.setFontSize(doc.getFontSize() - 1);
        doc.text(resources.productSheetSpecificationFooterText.replace('{manufacturer}', manufacturer), 5, 239);

        // Next page
        doc.addPage();

        // Print header
        doc.setDrawColor('black');
        doc.setFontSize(9);
        doc.text(resources.productSheetHeader.replace('{productNumber}', productNumber), 5, 5);
        doc.text(resources.productSheetHeaderRight.replace('{url}', url).replace('{timestamp}', timestamp), doc.internal.pageSize.width - 5, 5, {
            align: 'right'
        });
        doc.line(5, 7, doc.internal.pageSize.width - 5, 7);

        doc.setDrawColor('#CECECE');

        doc.setFont(doc.getFont().fontName, 'bold');
        doc.setFontSize(originalFontSize - 6);
        doc.text(resources.productSheetExhibitionLabel, 5, 14);

        doc.setFontSize(doc.getFontSize() - 3);
        doc.setFont(doc.getFont().fontName, 'normal');
        doc.setFillColor('#F0EDE6');
        doc.rect(5, 20, 100, 35, 'F');
        doc.setFillColor(originalFillColor);

        doc.setFontSize(originalFontSize - 2);
        (doc.splitTextToSize(productName, 90) as string[]).forEach((x, i) => {
            doc.text(x, 8, 27 + (i * 5));
        });

        doc.setFontSize(originalFontSize - 8);
        const allergensExcludingFreeFrom = allergens?.attributes
            .filter(x => x.value === locale.labels.yes || x.value === locale.labels.canContains);
        if (allergensExcludingFreeFrom && allergensExcludingFreeFrom.length > 0) {
            doc.text(`${locale.groups.allergens}:`, 8, 36.5);
            allergensExcludingFreeFrom?.forEach((x, i) => {
                const row = Math.floor(i / 4);
                doc.text(x.name, 9 + (row * 22), 41 + ((i - (row * 4)) * 4));
            });
        }

        doc.setFontSize(doc.getFontSize() + 2);
        doc.setFont(doc.getFont().fontName, 'bold');
        doc.text(locale.groups.ingredients, 5, 66);
        doc.setFont(doc.getFont().fontName, 'normal');
        (doc.splitTextToSize(ingredients, doc.internal.pageSize.width - 10) as string[]).forEach((x, i) => {
            doc.text(x, 5, 70 + (i * 4));
        });

        // Nutritional content table on second page
        doc.setFont(doc.getFont().fontName, 'bold');
        doc.text(locale.groups.nutrients, 100 + 7, 22);
        doc.setFont(doc.getFont().fontName, 'normal');

        nutritionalContentY = 26;
        nutritionalContent?.attributes.forEach((x, i) => {
            const xPos = 100 + 7;
            if ((i + 1) % 2 === 0) {
                doc.setFillColor('#F2F2F0');
                doc.rect(xPos, nutritionalContentY - 2.6, halfWidth - 10, 3, 'F');
            }
            doc.text(x.name, xPos, nutritionalContentY);
            doc.text(x.value || '-', xPos + 60, nutritionalContentY);
            nutritionalContentY += 3.5;
        });
        doc.setFillColor(originalFillColor);

        doc.setFont(doc.getFont().fontName, 'bold');
        doc.setFontSize(originalFontSize - 6);
        doc.text(resources.productSheetStockLabel, 5, 102);

        doc.setFontSize(originalFontSize - 3);
        doc.text(`${productNumber} ${productName}`, 5, 115);

        const canvas = document.createElement('canvas');
        // Paint barcode to canvas
        JsBarcode(canvas, productNumber, {
            displayValue: false, // Don't print any text
            height: 40
        });
        (doc as any).addImage(canvas, 'JPEG', 5, 116);

        doc.setFont(doc.getFont().fontName, 'normal');
        doc.setFontSize(originalFontSize - 7);
        let warehouseDetails = 121;

        doc.text(resources.productSheetSalesUnit, 60, warehouseDetails);
        doc.text(salesUnit, 120, warehouseDetails);
        warehouseDetails += 4;

        doc.text(resources.productSheetQuantityInPack, 60, warehouseDetails);
        doc.text(quantity, 120, warehouseDetails);
        warehouseDetails += 4;

        doc.text(resources.productSheetStorage, 60, warehouseDetails);
        doc.text(temperatureZone || '-', 120, warehouseDetails);
        warehouseDetails += 4;

        doc.text(locale.labels.brand, 60, warehouseDetails);
        doc.text(brand || locale.notFound, 120, warehouseDetails);
        warehouseDetails += 4;

        doc.text(locale.labels.manufacturer, 60, warehouseDetails);
        (doc.splitTextToSize(manufacturer, 60) as string[]).forEach((x) => {
            doc.text(x, 120, warehouseDetails);
            warehouseDetails += 3;
        });
        warehouseDetails += 1;

        doc.text(resources.productSheetSupplierItemNumber, 60, warehouseDetails);
        doc.text(manufacturerProductNumber, 120, warehouseDetails);
        warehouseDetails += 4;

        doc.setDrawColor('#D9D9D9');

        // Utstillingsetikett guide lines
        // Top left
        doc.line(5, 15, 5, 19);
        doc.line(1, 20, 5, 20);
        // Top right
        doc.line(doc.internal.pageSize.width - 5, 15, doc.internal.pageSize.width - 5, 19);
        doc.line(doc.internal.pageSize.width - 1, 20, doc.internal.pageSize.width - 5, 20);
        // Bottom left
        doc.line(5, 90, 5, 94);
        doc.line(1, 89, 5, 89);
        // Bottom right
        doc.line(doc.internal.pageSize.width - 5, 90, doc.internal.pageSize.width - 5, 94);
        doc.line(doc.internal.pageSize.width - 1, 89, doc.internal.pageSize.width - 5, 89);

        // Vareetikett guide lines
        // Top left
        doc.line(5, 104, 5, 108);
        doc.line(1, 109, 5, 109);
        // Top right
        doc.line(doc.internal.pageSize.width - 5, 104, doc.internal.pageSize.width - 5, 108);
        doc.line(doc.internal.pageSize.width - 1, 109, doc.internal.pageSize.width - 5, 109);
        // Bottom left
        doc.line(5, 138, 5, 144);
        doc.line(1, 137, 5, 137);
        // Bottom right
        doc.line(doc.internal.pageSize.width - 5, 138, doc.internal.pageSize.width - 5, 144);
        doc.line(doc.internal.pageSize.width - 1, 137, doc.internal.pageSize.width - 5, 137);

        return { productNumber, pdf: doc };
    }

    private __getFormattedDate(): string {
        const date = new Date();
        const year = date.getFullYear().toString();
        const month = date.getMonth().toString().padStart(2, '0');
        const day = date.getDay().toString().padStart(2, '0');
        const hour = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}.${month}.${year} kl. ${hour}:${minutes}`;
    }

    private __getImageNaturalSizeAsync(productImg: HTMLImageElement): Promise<{ width: number, height: number }> {
        return new Promise((resolve, reject) => {
            productImg.onload = () => {
                resolve({ width: productImg.naturalWidth, height: productImg.naturalHeight });
            };
            productImg.onerror = reject;
        });
    }

    private __scaleToMax60(width: number, height: number): { width: number, height: number } {
        const maxDimension = 60;

        // Determine if width or height is the larger dimension
        if (width > height) {
            const scale = maxDimension / width;
            return {
                width: maxDimension,
                height: Math.round(height * scale)
            };
        } else {
            const scale = maxDimension / height;
            return {
                width: Math.round(width * scale),
                height: maxDimension
            };
        }
    }
}

export default DlvryProductSheet;
